<template>
  <div>
    <!-- FOOTER BUSINESS REPORT -->
    <BrFooter v-if="isBusinessReport" />

    <!-- FOOTER -->
    <footer v-else class="bg-blue-500 px-6 py-10 text-white sm:p-12">
      <!-- LOGO -->
      <NuxtLink
        :to="localePath('/')"
        :aria-label="$t('aria_labels.home')"
      >
        <IconLogo class="h-5 sm:h-6" />
      </NuxtLink>

      <ul
        class="
          mt-11 grid w-full grid-cols-1 gap-y-5
          lg:grid-cols-4
          sm:grid-cols-[repeat(auto-fill,_minmax(9.25rem,_1fr))] sm:gap-10
          xl:grid-cols-6
        "
      >
        <!-- ADDRESS -->
        <li class="typo-body-xl-fat max-sm:pb-9">
          <!-- Address -->
          <a
            :href="globals?.general.link_address"
            target="_blank"
          >
            <address class="mb-8 not-italic transition-colors hover:text-blue-100" v-html="globals?.general.address" />
          </a>

          <!-- Phone -->
          <a class="mb-1 block w-fit transition-colors hover:text-blue-100" :href="`tel:${globals?.general.phone}`">
            {{ globals?.general.phone }}
          </a>

          <!-- Email -->
          <a class="mb-8 block w-fit transition-colors hover:text-blue-100" :href="`mailto:${globals?.general.email}`">
            {{ globals?.general.email }}
          </a>

          <!-- Portal Link -->
          <AppButton
            :to="globals?.general.portal_link"
            variant="outlined"
            size="sm"
            class="sm:-translate-x-6"
          >
            {{ globals?.general.portal_link_text }}
          </AppButton>
        </li>

        <!-- NAVIGATION -->
        <li
          v-for="navItem in globals?.footer.tree"
          :key="navItem.page.id"
          class="typo-body-xl-fat router-link-exact"
        >
          <!-- Desktop -->
          <div class="hidden sm:block">
            <!-- Top Level -->
            <Component
              :is="divOrNuxtLink(navItem.page.url)"
              :class="{ 'transition-colors hover:text-blue-100': navItem.page.url }"
              v-bind="navItem.page.url ? { to: navItem.page.url, target: useIsExternalUrl(navItem.page.url) ? '_blank' : '_self' } : {}"
            >
              {{ navItem.page.title }}
            </Component>

            <!-- Sub Level -->
            <ul class="mt-3 space-y-2 typo-body-xl">
              <li v-for="child in navItem.children" :key="child.page.id">
                <NuxtLink
                  class="transition-colors hover:text-blue-100"
                  :to="child.page.url"
                  :target="useIsExternalUrl(child.page.url) ? '_blank' : '_self'"
                >
                  {{ child.page.title }}
                </NuxtLink>
              </li>
            </ul>
          </div>

          <!-- Mobile -->
          <NavLinkMobile :nav-item="navItem" class="sm:hidden" />
        </li>
      </ul>

      <div
        class="
          mt-16 grid w-full grid-cols-1 gap-9
          sm:grid-cols-[repeat(auto-fill,_minmax(12.5rem,_1fr))] sm:gap-10
        "
      >
        <!-- SOCIAL MEDIA LINKS -->
        <ul
          v-if="globals?.general?.social_media_channels"
          class="flex flex-wrap gap-x-3 gap-y-2 sm:gap-x-6 sm:gap-y-4"
        >
          <li v-for="social in globals?.general.social_media_channels" :key="social.id" class="shrink-0">
            <a
              :href="social.link"
              target="_blank"
              :aria-label="social.channel.label"
              class="block size-7 rounded-full bg-white p-1 transition-transform hover:scale-125"
            >
              <Component :is="`Icon${social.channel.value}`" class="size-full text-blue-500" />
            </a>
          </li>
        </ul>

        <!-- META LINKS -->
        <ul class="flex flex-wrap gap-x-10 gap-y-4 sm:col-span-4 sm:gap-x-12 sm:gap-y-6">
          <li v-for="meta in globals?.footer_meta.tree" :key="meta.page.id">
            <NuxtLink class="transition-colors hover:text-blue-100" :to="meta.page.url" :target="useIsExternalUrl(meta.page.url) ? '_blank' : '_self'">
              {{ meta.page.title }}
            </NuxtLink>
          </li>

          <li>
            <!-- OneTrust Cookies Settings button start -->
            <ClientOnly>
              <button id="ot-sdk-btn" type="button" class="ot-sdk-show-settings">
                <span class="transition-colors hover:text-blue-100">{{ $t('cookie_settings') }}</span>
              </button>
            </ClientOnly>
            <!-- OneTrust Cookies Settings button end -->
          </li>
        </ul>
      </div>
    </footer>
  </div>
</template>

<script lang="ts" setup>
const globals = inject(GlobalsKey, null)
const isBusinessReport = useIsBusinessReport()
const localePath = useLocalePath()

function divOrNuxtLink(url: string) {
  if (url) {
    return resolveComponent('NuxtLink')
  }
  return 'div'
}
</script>

<style>
.router-link .router-link-active {
  @apply underline underline-offset-[0.625rem] sm:decoration-2 decoration-1;
}

.router-link-exact .router-link-exact-active {
  @apply underline underline-offset-[0.375rem] sm:decoration-2 decoration-1;
}
</style>
